import dayjs from 'dayjs'
import Link from 'next/link'
import {
  CompanySubscriptionSummary,
  CustomerStatus,
  CustomerStatuses,
} from 'common/types/responses'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'

import {
  getSubscriptions,
  useGetSubscriptionsQuery,
} from 'common/services/useSubscription'
import Stats from './Stats'
import Icon from 'project/Icon'
import PlanName from './PlanName'
import KYCStatus from './KYCStatus'
import { getStore } from 'common/store'
import Loader from 'components/base/Loader'
import Pagination from 'components/Pagination'
import Button from 'components/base/forms/Button'
import { ErrorMessage } from 'components/base/Messages'
import TableFilter from 'components/tables/TableFilter'
import dateAndTime from 'common/utils/date/dateAndTime'
import { fromParam, toParam } from 'common/utils/urls/param'
import DropdownItem from 'components/base/forms/DropdownItem'
import CancelSubscription from 'components/CancelSubscription'
import { FilterInputType } from 'components/tables/FilterInput'
import EllipsisDropdown from 'components/base/EllipsisDropdown'
import { usePermission, useUser } from 'common/services/useUser'
import NoResultsMessage from 'components/tables/NoResultsMessage'
import ContentContainer from 'components/tables/ContentContainer'
import DateRangePicker from 'components/base/forms/date/DateRangePicker'
import { trackCancelSubscription } from 'common/utils/track-subscription'
import { useGetGroupQuery, useGetGroupsQuery } from 'common/services/useGroup'
import { Req } from 'common/types/requests'
import Checkbox from 'components/base/forms/Checkbox'
import { useGetTeamMembersQuery } from 'common/services/useTeamMember'
import { camelCase } from 'common/utils/string/camelCase'
import ReassignCustomer from 'components/ReassignCustomer'
import ModalDefault from 'components/base/ModalDefault'
import CreateEditGroup from './CreateEditGroup'

type CustomerTableType = {
  onSelectChange?: (v: boolean) => void
  group?: number
  hideFilter?: boolean
  filter?: Partial<Req['getSubscriptions']>
  cta?: (v: CompanySubscriptionSummary) => ReactNode
}

const downloadCSV = (csvString: string, filename: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  a.click()
  window.URL.revokeObjectURL(url)
}

const SubscriptionTable: FC<CustomerTableType> = ({
  cta,
  filter = {},
  group,
  hideFilter,
  onSelectChange,
}) => {
  const params = fromParam()
  const [status, setStatus] = useState<CustomerStatus[]>(
    params.status ? params.status.split(',') : [],
  )
  const [groups, setGroups] = useState<number[]>(
    params.groups
      ? params.groups.split(',')?.map((v) => {
          if (isNaN(parseInt(v))) {
            return v
          }
          return parseInt(v)
        })
      : [],
  )
  const [showSelection, setShowSelection] = useState(false)

  const [members, setMembers] = useState<number[]>(
    params.members ? params.members.split(',')?.map(parseInt) : [],
  )
  const [selection, setSelection] = useState<number[]>()
  useEffect(() => {
    setSelection([])
  }, [showSelection])
  const [showEditGroup, setShowEditGroup] = useState(false)

  const { data: user } = useUser({})
  const [searchInput, setSearchInput] = useState(params.q || '')
  const [dateFrom, setDateFrom] = useState(params.date_from || undefined)
  const [dateTo, setDateTo] = useState(params.date_to || undefined)
  const [page, setPage] = useState(1)
  const canEdit = usePermission('WRITE')
  const isGroupUser = usePermission('GROUP_ONLY')
  const router = useRouter()
  const [activeCustomerId, setActiveCustomerId] = useState<number>()
  const [activeSubscription, setActiveSubscription] =
    useState<CompanySubscriptionSummary>()
  const [activeSubscriptionId, setActiveSubscriptionId] = useState<number>()
  const [showCancel, setShowCancel] = useState(false)
  const { data: _allGroups } = useGetGroupsQuery({}, { skip: isGroupUser })
  const { data: allMembers } = useGetTeamMembersQuery({})
  const allGroups = isGroupUser
    ? user.groups.map((v) => ({ ...v, id: v.group_id }))
    : _allGroups?.results
  const [isExporting, setIsExporting] = useState(false)
  const [showReassignment, setShowReassignment] = useState(false)
  const { data: groupDetail } = useGetGroupQuery(
    { id: group },
    { skip: !group },
  )
  const groupsAndMemberGroups = useMemo(() => {
    if (!members?.length || !allGroups?.length) {
      return groups
    }
    const addedGroups = allGroups
      ?.filter((v) => {
        if (groups.includes(v.id)) {
          return null
        }

        if (v.users?.find((v) => members.includes(v.user_id))) {
          return true
        }
      })
      .map((v) => v.id)
    return groups.concat(addedGroups)
  }, [allGroups, members, groups])

  const filters = useMemo(() => {
    const filteredMembers = allMembers?.results?.filter(
      (member) =>
        allGroups?.find(
          (group) =>
            group.users?.find((groupUser) => groupUser.user_id === member.id),
        ),
    )
    return [
      isGroupUser
        ? null
        : {
            getLabel: (option: number) => {
              const member = allMembers?.results?.find((v) => v.id === option)
              return `${member?.first_name} ${member?.last_name}`
            },
            getSearch: (option: number) => {
              const member = allMembers?.results?.find((v) => v.id === option)
              return `${member?.first_name} ${member?.last_name} ${member?.email}`
            },
            label: 'Members',
            onChange: filteredMembers?.length ? setMembers : () => {},
            options: filteredMembers?.length
              ? (filteredMembers.map((v) => v.id) as any)
              : [null],
            renderOption: (option: number) => {
              if (!filteredMembers?.length) {
                return (
                  <div className='d-flex text-center text-muted flex-column gap-0'>
                    You have no groups that contain members.
                    <br />
                    Create them in the Team page.
                  </div>
                )
              }
              const member = allMembers?.results?.find((v) => v.id === option)
              return (
                <div className='d-flex mb-2 flex-column gap-0'>
                  <div className='fw-semibold'>
                    {member?.first_name} {member?.last_name}
                  </div>
                  <div className='fs-sm text-muted lh-1'>{member?.email}</div>
                </div>
              )
            },
            value: members,
          },
      {
        label: 'Groups',
        onChange: (v) => {
          if (v[v.length - 1] === 'none') {
            setGroups(['none'])
            setMembers([])
          } else {
            setGroups(v.filter((v) => v !== 'none'))
          }
        },
        options: (isGroupUser ? [] : ['none']).concat(
          allGroups?.map((v) => v.id),
        ) as any,
        renderOption: (option: number | string) => {
          if (!allGroups?.length) {
            return (
              <div className='d-flex  text-muted flex-column gap-0'>
                You have no groups.
                <br />
                Create them in the Team page.
              </div>
            )
          }
          return typeof option === 'string'
            ? camelCase(option)
            : allGroups?.find((v) => v.id === option)?.name
        },
        value: groups,
      },
      {
        label: 'Status',
        onChange: setStatus,
        options: CustomerStatuses as any,
        value: status,
      },
    ].filter((v) => !!v) as FilterInputType['filters']
  }, [status, groups, members, allMembers, allGroups])

  const { data, error, isFetching, isLoading } = useGetSubscriptionsQuery({
    date_from: dateFrom,
    date_to: dateTo,
    groups: groupsAndMemberGroups,
    page,
    page_size: 20,
    q: searchInput,
    status,
    ...filter,
  } as any)

  useEffect(() => {
    router.replace(
      `${document.location.pathname}?${toParam({
        date_from: dateFrom,
        date_to: dateTo,
        groups: groups.join(','),
        members: members.join(','),
        page,
        q: searchInput,
        status: status.join(','),
      })}`,
    )
    //eslint-disable-next-line
  }, [searchInput, dateFrom, dateTo, page, members, groups, status])

  const handleExport = () => {
    setIsExporting(true)
    getSubscriptions(getStore(), {
      date_from: dateFrom,
      date_to: dateTo,
      export: true,
      page,
      page_size: data?.count || 20,
      q: searchInput,
      status,
    })
      .then((res) => {
        setIsExporting(false)
        if (!res.data) return
        downloadCSV(
          res.data,
          `customers_export_${dayjs().format('YYYY-MM-DD')}.csv`,
        )
      })
      .catch((error) => {
        setIsExporting(false)
        console.error('Export error:', error)
      })
  }
  return isLoading && !data ? (
    <div className='text-center'>
      <Loader />
    </div>
  ) : (
    <>
      {hideFilter !== true && (
        <div key='filter'>
          {canEdit && (
            <div className='d-none d-xl-block'>
              <Stats />
            </div>
          )}

          <div className='d-flex align-items-center justify-content-between gap-4'>
            <div className='flex-1'>
              <TableFilter
                placeholder='Search subscriptions...'
                customFilters={
                  <div className='me-4'>
                    <DateRangePicker
                      selectRange
                      showDoubleView
                      placeholder='Filter by date'
                      showNeighboringMonth={false}
                      onChange={(v) => {
                        // @ts-ignore
                        if (v?.[0] && v?.[1]) {
                          // @ts-ignore
                          setDateFrom(dayjs(v[0]).format('YYYY-MM-DD'))
                          // @ts-ignore
                          setDateTo(dayjs(v[1]).format('YYYY-MM-DD'))
                        } else {
                          setDateFrom(undefined)
                          setDateTo(undefined)
                        }
                      }}
                      value={
                        dateFrom && dateTo
                          ? [
                              dayjs(dateFrom, 'YYYY-MM-DD').toISOString(),
                              dayjs(dateTo, 'YYYY-MM-DD').toISOString(),
                            ]
                          : null
                      }
                    />
                  </div>
                }
                filters={filters}
                onChange={setSearchInput}
                value={searchInput}
              />
            </div>
          </div>
        </div>
      )}
      {!showSelection && (
        <div className='d-flex justify-content-end mb-5 gap-4'>
          {canEdit && (
            <Link href={`/customers/create${group ? `?groups=${group}` : ''}`}>
              <Button>
                <div className='d-flex gap-2 align-items-center'>
                  Add Customer
                  <Icon fill='white' name='add-user' />
                </div>
              </Button>
            </Link>
          )}
          {!!data?.results.length && !!setShowSelection && canEdit && (
            <Button theme='tertiary'>
              <div
                onClick={() => setShowSelection(true)}
                className='d-flex gap-2 align-items-center'
              >
                Manage Customers
                <Icon fill='white' name='user-settings' />
              </div>
            </Button>
          )}
          {group ? (
            <>
              <Button theme='secondary' onClick={() => setShowEditGroup(true)}>
                <div className='d-flex gap-2 align-items-center'>
                  Edit Group
                  <Icon name='edit' />
                </div>
              </Button>
              {!!groupDetail && (
                <ModalDefault
                  isOpen={showEditGroup}
                  title='Edit Group'
                  onDismiss={() => setShowEditGroup(false)}
                  toggle={() => setShowEditGroup(!showEditGroup)}
                >
                  <CreateEditGroup
                    onComplete={() => {
                      setShowEditGroup(false)
                    }}
                    group={groupDetail}
                  />
                </ModalDefault>
              )}
            </>
          ) : (
            <Button
              theme='secondary'
              onClick={handleExport}
              disabled={isExporting}
            >
              <div className='d-flex gap-2 align-items-center'>
                Export CSV
                <Icon name='download' />
              </div>
            </Button>
          )}
        </div>
      )}
      {!!showSelection && (
        <div className='d-flex align-items-center justify-content-end mb-5 gap-4'>
          <span>
            <strong>{selection.length}</strong> selected
          </span>
          <Button
            style={{ width: 140 }}
            onClick={() => {
              setShowSelection(false)
              setSelection([])
            }}
            theme='secondary'
          >
            Cancel
          </Button>
          <Button
            style={{ width: 140 }}
            disabled={selection.length === 0}
            onClick={() => setShowReassignment(true)}
          >
            <div className='d-flex gap-2 align-items-center'>
              Reassign
              <Icon fill='white' name='team' />
            </div>
          </Button>
        </div>
      )}
      <ErrorMessage className='mb-4'>{error}</ErrorMessage>

      <ContentContainer className='pb-1'>
        <table
          className={classNames({
            disabled: isFetching,
          })}
        >
          <thead>
            <tr style={{ verticalAlign: 'middle' }}>
              {showSelection && (
                <th className='text-nowrap'>
                  <Checkbox
                    id='select-all'
                    onChange={() => {
                      if (data) {
                        if (selection?.length === data?.results?.length) {
                          setSelection([])

                          onSelectChange?.([])
                        } else {
                          setSelection(data!.results)
                          onSelectChange?.(data!.results)
                        }
                      }
                    }}
                    checked={selection?.length === data?.results?.length}
                  />
                </th>
              )}
              <th className='text-nowrap'>Company</th>
              <th className='d-md-table-cell d-none text-nowrap'>
                Subscription
              </th>
              <th
                style={{ width: 180 }}
                className='d-md-table-cell d-none text-nowrap'
              >
                Date
              </th>
              <th
                style={{ width: 250 }}
                className='d-md-table-cell d-none text-nowrap'
              >
                Group
              </th>
              <th style={{ width: 100 }} className='text-nowrap'>
                Status
              </th>
              <th style={{ width: 50 }} className='text-nowrap'></th>
            </tr>
          </thead>
          <tbody>
            <NoResultsMessage data={data} />
            {data?.results?.map((subscription) => {
              const goSubscription = () => {
                router.push(
                  `/customers/${encodeURIComponent(
                    `${subscription.company_id}`,
                  )}/subscriptions/${encodeURIComponent(subscription.id)}`,
                )
              }
              const goCompany = () => {
                router.push(
                  `/customers/${encodeURIComponent(
                    `${subscription.company_id}`,
                  )}`,
                )
              }
              return (
                <tr style={{ verticalAlign: 'middle' }} key={subscription.id}>
                  {showSelection && (
                    <td>
                      <Checkbox
                        id={`select-${subscription.id}`}
                        onChange={() => {
                          if (selection?.includes(subscription)) {
                            setSelection(
                              selection.filter((v) => v.id !== subscription.id),
                            )
                            onSelectChange?.(
                              selection.filter((v) => v.id !== subscription.id),
                            )
                          } else {
                            setSelection(
                              (selection || []).concat([subscription]),
                            )
                            onSelectChange?.(
                              (selection || []).concat([subscription]),
                            )
                          }
                        }}
                        checked={
                          !!selection?.find((v) => v.id === subscription.id)
                        }
                      />
                    </td>
                  )}
                  <td
                    onClick={goCompany}
                    className='d-none fw-bold cursor-pointer d-md-table-cell hover-primary'
                  >
                    {subscription.company_name}
                  </td>
                  <td className='d-table-cell d-md-none'>
                    <div
                      onClick={goCompany}
                      className='fw-bold pb-2 hover-primary'
                    >
                      {subscription.company_name}
                    </div>
                    <div
                      onClick={goSubscription}
                      className='fw-semibold hover-primary d-block d-md-none'
                    >
                      <PlanName id={subscription.plan_id} />
                      <div>{subscription.email_address}</div>
                    </div>
                  </td>
                  <td
                    onClick={goSubscription}
                    className='d-none hover-primary cursor-pointer hover-target d-md-table-cell'
                  >
                    <PlanName id={subscription.plan_id} />
                  </td>
                  <td className='d-none d-md-table-cell'>
                    {dateAndTime(subscription.created_at)}
                  </td>
                  <td>{subscription?.groups?.map((v) => v.name).join(', ')}</td>
                  <td>
                    <KYCStatus subscription={subscription} />
                  </td>
                  <td>
                    {cta ? (
                      cta(subscription)
                    ) : (
                      <EllipsisDropdown>
                        <DropdownItem
                          icon={<Icon name='edit' />}
                          onClick={goSubscription}
                        >
                          Edit Subscription
                        </DropdownItem>
                        <DropdownItem
                          icon={<Icon name='delete' fill='danger' />}
                          onClick={() => {
                            setActiveCustomerId(subscription.company_id)
                            setActiveSubscription(subscription)
                            setActiveSubscriptionId(subscription.id)
                            setShowCancel(true)
                          }}
                        >
                          <span className='text-danger'>
                            Cancel Subscription
                          </span>
                        </DropdownItem>
                      </EllipsisDropdown>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ContentContainer>
      {!!showSelection && (
        <ReassignCustomer
          from={group}
          isOpen={showReassignment}
          setIsOpen={setShowReassignment}
          selectedCustomers={selection}
          onComplete={() => {
            setPage(1)
            setSelection([])
            setShowSelection(false)
          }}
        />
      )}
      {!!activeCustomerId && (
        <CancelSubscription
          isOpen={showCancel}
          setIsOpen={setShowCancel}
          customerId={activeCustomerId!}
          id={activeSubscriptionId!}
          onCancel={() => {
            trackCancelSubscription(activeSubscription!, user!)
          }}
        />
      )}

      {!!data && <Pagination data={data} onChange={setPage} />}
    </>
  )
}

export default SubscriptionTable
